import React, {useEffect, useMemo} from 'react';
import {Box, Button} from '@mui/material';
import SourceDestination from '../CreateRecommend/RecommendSourceDestination/SourceDestination';
import {RecommendProps} from '../CreateRecommend/RecommendSourceDestination/validators';
import {RecommendPlanFormsStyled} from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';
import {useAccountContext} from '../../../contexts/AccountContext';
import {
  recommendFieldsType,
  planAndSubplanAmounts,
} from '../CreateRecommend/recommendFields';
import {transferFieldsType} from './transferFields';
import type {setUpNewFields} from '../RecommendsSetUpNew/setUpNewFields';
import {useResetReccs} from '../CreateRecommend/RecommendSourceDestination/useResetReccs';
import {OVRecommendActionEnum, BusinessUnitEnum} from '../../../schema-types/globalTypes';
import {
  updateTransferSourceIdFactory,
  loadSavedTransferPlans,
  setTransferCombinedAmounts,
  transferSourcePots,
  transferShellPlans,
  transferDestinationPots,
  calculateTransferFirstSubplanAmounts,
} from './TransferHelpers';
import {TransferSourcePlanForm, TransferShellPlanForm} from './TransferSourcePlanForm';
import {
  TransferDestinationPlanForm,
  TransferNewPlanForm,
} from './TransferDestinationPlanForm';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {useRecommendContext} from '../CreateRecommend/CreateRecommendHelpers';
import {reccNonEditable} from '../CreateRecommend/RecommendSourceDestination/CreateRecommendFooter';
import {CASH_OBJ} from '../../../utils/potsFilter';
import {useTransferAmounts} from './useTransferAmounts';
import {
  useAutoForm,
  useAutoField,
  useLoadedAutoField,
} from '../../../components/AutoForm/useAutoForm';
import {
  changeTransferDestination,
  resetTransferSourcesOnPlanTypeChange,
  resetDestinationIfInSources,
  initTransferDefaults,
  addShellSourcePlan,
  useResetTransferSourcesOnPlanTypeChange,
  useResetDestinationIfInSources,
  useInitTransferDefaults,
  useLoadedShellProducts,
} from '../hooks/transferHooks';

export {
  changeTransferDestination,
  resetTransferSourcesOnPlanTypeChange,
  resetDestinationIfInSources,
  initTransferDefaults,
  addShellSourcePlan,
  useResetTransferSourcesOnPlanTypeChange,
  useResetDestinationIfInSources,
  useInitTransferDefaults,
};

export const Transfer: React.FC<RecommendProps> = ({
  sourcePots,
  sourceDestinationLabels,
  contactName,
}) => {
  const {setLoaded} = useLoadedAutoField();
  const {accountType} = useAccountContext();
  const {recommendDetails, loadingOvRecommend, recommendReviewId, isSPPDA} =
    useRecommendContext();
  const {values, setFieldValue} = useAutoForm<recommendFieldsType>();
  const [transferFieldObj, transferFieldMeta, transferFieldHelpers] = useAutoField<
    transferFieldsType | undefined
  >('transfer');
  const [
    transferDestAccountFieldObj,
    transferDestAccountFieldMeta,
    transferDestAccountFieldHelpers,
  ] = useAutoField<transferFieldsType['destinationAccount']>(
    'transfer.destinationAccount'
  );

  const [
    transferDestinationFieldObj,
    transferDestinationFieldMeta,
    transferDestinationFieldHelpers,
  ] = useAutoField<transferFieldsType['destinationValues']>('transfer.destinationValues');
  const [transferSourcesFieldObj, transferSourcesFieldMeta, transferSourcesFieldHelpers] =
    useAutoField<transferFieldsType['transferSources'] | undefined>(
      'transfer.transferSources'
    );
  useResetReccs(OVRecommendActionEnum.TRANSFER);
  const [setUpNewFieldObj, setUpNewFieldMeta, setUpNewFieldHelpers] = useAutoField<
    setUpNewFields['newPlan'] | undefined
  >('transfer.newPlan');
  const {mainPlanType} = values;
  const shellProducts = useLoadedShellProducts();

  const [_, __, setUpNewFirstSubplanClientAmountFieldHelpers] = useAutoField<
    planAndSubplanAmounts['singleClientAmount'] | undefined
  >('transfer.newPlan.subplan0.singleClientAmount');

  const checkedListSources: {
    normalSources: string[];
    shellPlans: string[];
    total: string[];
  } = useMemo(() => {
    if (!transferSourcesFieldObj.value) {
      return {
        normalSources: [],
        shellPlans: [],
        total: [],
      };
    }
    const total = Object.keys(transferSourcesFieldObj.value || {});
    const normalSources = total.filter((x) => !x.includes('shell'));
    const shellPlans = total.filter((x) => x.includes('shell'));
    return {
      normalSources,
      total,
      shellPlans,
    };
  }, [transferSourcesFieldObj.value]);

  const changeDestination = (event: React.ChangeEvent<any> | null, value: string) => {
    changeTransferDestination({
      value,
      event,
      sourcePots,
      transferDestAccountFieldHelpers,
      transferDestinationFieldHelpers,
    });
  };
  const changeSourceIdObj = updateTransferSourceIdFactory({
    transferSourcesFieldObj,
    sourcePots,
    transferSourcesFieldHelpers,
    ovrecommendid: recommendDetails?.id || false,
  });

  useResetTransferSourcesOnPlanTypeChange({transferSourcesFieldHelpers});

  // useEffect(() => {
  //   if (mainPlanType === 'pension' && reasonOptions.length) {
  //     if (!loadingOvRecommend && !recommendDetails) {
  //       const pensionSwitch = reasonOptions.find((x) =>
  //         x.label.toLocaleLowerCase().includes('pension switch')
  //       );
  //       if (pensionSwitch) {
  //         setFieldValue(transferReasonField.id, pensionSwitch.value, true);
  //       }
  //     }
  //   }
  // }, [mainPlanType, recommendDetails, loadingOvRecommend, reasonOptions.length]);

  useResetDestinationIfInSources({
    checkedListSources,
    transferDestAccountFieldObj,
    transferDestAccountFieldHelpers,
  });

  const showAllPensions = useMemo(() => {
    if (mainPlanType !== 'pension') {
      return true;
    }
    return values.transfer?.isDependantsPension || values.transfer?.isPensionSharing;
  }, [
    values.transfer?.isDependantsPension || values.transfer?.isPensionSharing,
    mainPlanType,
  ]);

  const sourcePotsFiltered = useMemo(() => {
    const pots = transferSourcePots({
      sourcePots,
      recommendDetails,
      mainPlanType,
      contact: values.contact,
      showAllPensions,
    });
    return pots;
  }, [sourcePots, recommendDetails, mainPlanType, values.contact, showAllPensions]);

  const shellPlansList = useMemo(() => {
    const plans = transferShellPlans({transferSources: transferSourcesFieldMeta.value});
    return plans;
  }, [transferSourcesFieldMeta.value]);

  useEffect(() => {
    calculateTransferFirstSubplanAmounts({
      mainPlanType,
      values,
      setUpNewFirstSubplanClientAmountFieldHelpers,
      setUpNewFieldObj,
    });
  }, [
    values.transfer?.combinedOneOffMovingAmount,
    // values.transfer?.newPlan?.newSubplanList,
    setUpNewFieldObj.value?.subplan1?.singleClientAmount,
    setUpNewFieldObj.value?.subplan1?.checked,
    mainPlanType,
  ]);

  useInitTransferDefaults();

  useEffect(() => {
    setTransferCombinedAmounts({setFieldValue, transferSourcesFieldObj});
  }, [transferSourcesFieldObj.value]);

  const destPotsFiltered = useMemo(() => {
    const pots = transferDestinationPots({
      transferSourcesFieldObj,
      sourcePots,
      recommendDetails,
      mainPlanType,
      isSppDa: accountType === BusinessUnitEnum.DA,
      showAllPensions,
      contact: values.contact,
    });
    return pots;
  }, [
    transferSourcesFieldObj,
    sourcePots,
    recommendDetails,
    mainPlanType,
    accountType === BusinessUnitEnum.DA,
    showAllPensions,
    values.contact,
  ]);

  useEffect(() => {
    if (loadingOvRecommend) {
      return;
    }
    loadSavedTransferPlans({
      recommendDetails,
      transferFieldHelpers,
      transferFieldObj,
      isSPPDA,
      // transferDestAccountFieldHelpers,
      // setUpNewFieldHelpers,
      // transferSourcesFieldHelpers,
      // transferSourcesFieldObj,
      // addNewSourcePlans,
    });
    setLoaded();
  }, [recommendDetails, loadingOvRecommend, isSPPDA]);
  useTransferAmounts();
  if (!values.mainPlanType) {
    return null;
  }

  return (
    <>
      <SourceDestination
        fromError={transferSourcesFieldMeta.error}
        toError={transferDestAccountFieldMeta.error}
        source={
          <>
            <RecommendPlanFormsStyled>
              {sourcePotsFiltered.map((pot, index) => (
                <TransferSourcePlanForm
                  key={pot.id}
                  pot={pot}
                  index={index}
                  sourcePots={sourcePots}
                  mainPlanType={mainPlanType}
                  transferSourcesFieldMeta={transferSourcesFieldMeta}
                  transferTo={transferDestAccountFieldObj.value}
                  reviewId={recommendReviewId}
                  isSelected={checkedListSources.total.includes(pot.id) || false}
                  handleChange={changeSourceIdObj}
                />
              ))}
              {shellPlansList.map((shellPlan, index) => (
                <TransferShellPlanForm
                  key={shellPlan.id}
                  shellPlan={shellPlan}
                  index={index}
                  isSelected={checkedListSources.total.includes(shellPlan.id) || false}
                  handleChange={changeSourceIdObj}
                />
              ))}
            </RecommendPlanFormsStyled>
            {reccNonEditable(recommendDetails) ? null : (
              <Box
                mt={4}
                pb={12}
                ml={2}
                display="flex"
                alignContent="center"
                justifyContent="center"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    addShellSourcePlan({
                      checkedListSources,
                      setFieldValue,
                      shellProducts: shellProducts || [],
                      mainPlanTypeValue: mainPlanType,
                    });
                  }}
                >
                  Add{' '}
                  {sourcePotsFiltered.filter((x) => x.id === CASH_OBJ.id).length
                    ? 'Another'
                    : ''}{' '}
                  New Source Plan
                </Button>
              </Box>
            )}
          </>
        }
        destination={
          <>
            <RecommendPlanFormsStyled>
              {destPotsFiltered.map((pot) => (
                <TransferDestinationPlanForm
                  key={pot.id}
                  pot={pot}
                  destPots={destPotsFiltered}
                  mainPlanType={mainPlanType}
                  values={values}
                  transferDestAccountFieldObj={transferDestAccountFieldObj}
                  transferSourcesFieldObj={transferSourcesFieldObj}
                  transferDestAccountFieldMeta={transferDestAccountFieldMeta}
                  recommendDetails={recommendDetails}
                  reviewId={recommendReviewId}
                  isSelected={transferDestAccountFieldObj.value === pot.id}
                  handleChange={changeDestination}
                />
              ))}
              <TransferNewPlanForm
                values={values}
                transferDestAccountFieldObj={transferDestAccountFieldObj}
                transferSourcesFieldObj={transferSourcesFieldObj}
                contactName={contactName}
                setUpNewFieldMeta={setUpNewFieldMeta}
                recommendDetails={recommendDetails}
                isSelected={transferDestAccountFieldObj.value === NEW_PLAN_ID}
                handleChange={changeDestination}
              />
            </RecommendPlanFormsStyled>
          </>
        }
        sourceLabel={sourceDestinationLabels?.first}
        destinationLabel={sourceDestinationLabels?.second}
      />
    </>
  );
};
